import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, TrendingUp, ArrowUpDown } from "lucide-react";
import { useState, useMemo } from "react";
import { AdesaoBar } from "@/components/dashboard";

type SortKey = "channelName" | "totalCompanies" | "totalEvaluations" | "concluidas" | "taxaConclusao" | "docsReady";

export default function SalviaCanais() {
  usePageTitle("Performance Canais");
  const { user } = useAuth();
  const { data, isLoading, error } = trpc.dashboards.salvia.channelPerformance.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
    refetchOnWindowFocus: false,
  });
  const [sortKey, setSortKey] = useState<SortKey>("totalCompanies");
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = useMemo(() => {
    if (!data?.channels) return [];
    return [...data.channels].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (typeof av === "string" && typeof bv === "string") return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [data, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Performance Canais">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground select-none"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-3 w-3" />
      </span>
    </th>
  );

  return (
    <SalviaLayout title="Dashboard — Performance Canais">
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Ranking de Canais</h3>
            <span className="text-xs text-muted-foreground">{sorted.length} canais</span>
          </div>
          {isLoading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : sorted.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum canal encontrado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <SortHeader label="Canal" field="channelName" />
                    <SortHeader label="Clientes" field="totalCompanies" />
                    <SortHeader label="Avaliações" field="totalEvaluations" />
                    <SortHeader label="Concluídas" field="concluidas" />
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pipeline</th>
                    <SortHeader label="Taxa Conclusão" field="taxaConclusao" />
                    <SortHeader label="Docs" field="docsReady" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((ch) => (
                    <tr key={ch.channelId} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-3 font-medium">{ch.channelName}</td>
                      <td className="px-3 py-3 tabular-nums">{ch.totalCompanies}</td>
                      <td className="px-3 py-3 tabular-nums">{ch.totalEvaluations}</td>
                      <td className="px-3 py-3 tabular-nums text-emerald-600 font-medium">{ch.concluidas}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-slate-500">{ch.pendentes}P</span>
                          <span className="text-blue-500">{ch.emAndamento}A</span>
                          <span className="text-red-500">{ch.bloqueadas}B</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 w-32">
                        <AdesaoBar completed={ch.concluidas} total={ch.totalEvaluations} />
                      </td>
                      <td className="px-3 py-3 tabular-nums">{ch.docsReady}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
